import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import { MenuItem, Select, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

export type TimeFilterValue = "thisMonth" | "lastMonth" | "last3Months" | "thisYear" | "";

interface TimeFilterDropdownProps {
  value: TimeFilterValue | null;
  onChange: (value: TimeFilterValue | null) => void;
  selectSx?: SxProps<Theme>;
}

export const TimeFilterDropdown: React.FC<TimeFilterDropdownProps> = ({ value, onChange, selectSx }) => {
  const { t } = useTranslation("ValuationListPage");
  const theme = useTheme();

  const timeFilterOptions: Array<{ value: TimeFilterValue; label: string }> = [
    { value: "", label: t("all") },
    { value: "thisMonth", label: t("thisMonth") },
    { value: "lastMonth", label: t("lastMonth") },
    { value: "last3Months", label: t("last3Months") },
    { value: "thisYear", label: t("thisYear") },
  ];

  const internalValue: TimeFilterValue = (value ?? "") as TimeFilterValue;

  const selectWidth =
    typeof selectSx === "object" && selectSx && "width" in selectSx
      ? (selectSx.width as string | number | undefined)
      : undefined;

  return (
    <Stack spacing={2}>
      <Typography variant="subText">{t("timeFilter")}</Typography>
      <Select
        value={internalValue}
        onChange={(event) => {
          const next = event.target.value as TimeFilterValue;
          onChange(next === "" ? null : next);
        }}
        displayEmpty
        sx={selectSx}
        MenuProps={{
          PaperProps: {
            sx: {
              width: "fit-content",
              minWidth: "100%",
              maxWidth: selectWidth,
            },
          },
        }}
        IconComponent={(props) => (
          <ExpandMoreOutlinedIcon
            {...props}
            sx={{
              ...props.sx,
              "&, & path": {
                color: `${theme.palette.primary.main} !important`,
                fill: `${theme.palette.primary.main} !important`,
              },
            }}
          />
        )}
        data-cy="date-filter"
      >
        {timeFilterOptions.map((opt) => (
          <MenuItem
            key={opt.value || "all"}
            value={opt.value}
            sx={{
              whiteSpace: "normal",
              wordWrap: "break-word",
            }}
          >
            {opt.label}
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
};
